import React, { useEffect, useMemo } from "react";
import { LazyComponent } from "../../../../hoc";
import { PrivatePropertyListPageSidebarFilterMenu } from "./PrivatePropertyListPageSidebarFilterMenu";
import { AuthModalWrapper } from "../../Auth/AuthModal";
import { PDAcknowledgmentModal } from "../../propertyDetail/v2Components/PDAcknowledgmentModal";
import {
  PropertyListPageWrapper,
  PropertyListViewWrapper,
} from "../../propertyListingV2/shared";
import { usePrivatePLSearch } from "./hooks/usePrivatePLSearch";
import { REQUEST_TYPES } from "../../propertyListingV2/constants/auction";
import { useRequestImages } from "../../propertyListingV2/hooks/useRequestImages";
import { useInfiniteScroll } from "../../propertyListingV2/hooks/useInfiniteScroll";
import { PropertyListHeaderView } from "../../propertyListingV2/shared";
import { usePLCommonStates } from "../../propertyListingV2/hooks/usePLCommonStates";
import { PropertyDataInterface } from "../../propertyListingV2/shared/PropertyCardGridView";

export type OptionType = {
  label: string;
  value: string;
};

interface PrivatePropertyListPageWrapperProps {
  cities: any;
  defaultLocation: any;
}

export const PrivatePropertyListPageWrapper: React.FC<
  PrivatePropertyListPageWrapperProps
> = ({ defaultLocation, cities }) => {
  const {
    router,
    isFilterOpen,
    setIsFilterOpen,
    isLoggedIn,
    authModalOpen,
    setAuthModalOpen,
    acknowledgementOpen,
    setAcknowledgementOpen,
    acknowledgmentErrorMessage,
    setAcknowledgmentErrorMessage,
    city,
    searchForm,
    withAuth,
    user,
  } = usePLCommonStates({ defaultLocation, cities });

  const addCityToUrl = () => {
    const city = searchForm?.value?.city?.value;

    if (city === undefined) return;

    if (city === "") {
      router.push("/private-properties");
      return;
    }

    router.push(`/private-properties?city=${city}`);
  };

  useEffect(() => {
    addCityToUrl();
  }, [
    searchForm?.value?.asset_type?.value,
    JSON.stringify(searchForm?.value?.sub_asset_type),
    searchForm?.value?.state?.value,
    searchForm?.value?.institutes,
    searchForm?.value?.city?.value,
    searchForm?.value?.locality?.length,
    searchForm?.value?.reserve_price,
    searchForm?.value?.auction_start_date_unix_timestamp,
    searchForm?.value?.filter_by?.value,
    searchForm?.value?.sort_by?.value,
    searchForm?.value?.listing_id_query,
    searchForm?.value?.ab_verified,
  ]);

  const { data, loading, noMore, fetchFirstPage, fetchNextPage } =
    usePrivatePLSearch({
      router,
      searchForm,
    });

  useInfiniteScroll({
    disabled: loading || noMore,
    onReachBottom: fetchNextPage,
  });

  useEffect(() => {
    if (router.isReady) {
      fetchFirstPage();
    }
  }, [
    router.isReady,
    searchForm?.value?.locality?.length,
    searchForm?.value?.city?.value,
  ]);

  const handleClearFilters = () => {
    searchForm.value.asset_type = "";
    searchForm.value.sub_asset_type = "";
    searchForm.value.asset_type = "";
    searchForm.value.state = "";
    searchForm.value.city = city || "";
    searchForm.value.locality = "";
    searchForm.value.reserve_price = "";
    searchForm.value.filter_by = "";
    searchForm.value.sort_by = "";
    searchForm.value.listing_id_query = "";
    searchForm.value.ab_verified = "";
  };

  const { reset: resetRequestingPropertyImagesStates, requestImages } =
    useRequestImages({
      withAuth,
      user,
      onSuccess: () => {
        setAcknowledgementOpen(true);
      },
      onError: () => {
        setAcknowledgementOpen(true);
        setAcknowledgmentErrorMessage(
          "Failed to request images. Please try again later.",
        );
      },
      propertyType: "private_property",
      requestType: REQUEST_TYPES.PRIVATE_PROPERTY_IMAGES,
    });

  const handleFiltersApply = () => {
    fetchFirstPage();
  };

  const handleRequestImagesClick = (property: PropertyDataInterface) => {
    if (!property?.listingId) return;
    requestImages(property?.listingId);
  };

  const handleSelectSortOption = (data: OptionType) => {
    if (data?.value === "all_properties") {
      handleClearFilters();
    }
    fetchFirstPage();
  };

  const handleClearFiltersHandler = () => {
    handleClearFilters();
    fetchFirstPage();
  };

  const sortByDropdownOptions = useMemo(
    () => [
      { label: "All Properties", value: "all_properties" },
      { label: "Price: Low to high", value: "reserve_price_asc" },
      { label: "Price: High to low", value: "reserve_price_desc" },
    ],
    [],
  );

  return (
    <>
      <PropertyListPageWrapper
        isFilterOpen={isFilterOpen}
        cities={cities}
        footerChildren={
          <>
            {noMore && (
              <LazyComponent
                importFunc={() => import("app-v2").then((mod) => mod.Footer)}
              />
            )}

            <AuthModalWrapper
              userLoggedin={isLoggedIn}
              authModelOpen={authModalOpen}
              setAuthModelOpen={setAuthModalOpen}
            />
            <PDAcknowledgmentModal
              acknowledgementOpen={acknowledgementOpen}
              setAcknowledgementOpen={setAcknowledgementOpen}
              errorMessage={acknowledgmentErrorMessage}
              onClose={() => {
                resetRequestingPropertyImagesStates();
              }}
            />
          </>
        }
      >
        <PropertyListHeaderView
          title="Private Properties"
          isFilterOpen={isFilterOpen}
          totalCount={searchForm?.value?.total}
          onSelectSortOption={handleSelectSortOption}
          onFilterToggle={() => setIsFilterOpen((v) => !v)}
          sortByDropdownOptions={sortByDropdownOptions}
          isLoading={loading}
        />

        <PrivatePropertyListPageSidebarFilterMenu
          filtersOpen={isFilterOpen}
          onClearFilters={handleClearFiltersHandler}
          onClose={() => setIsFilterOpen(false)}
          onFiltersApply={handleFiltersApply}
          setIsFilterOpen={setIsFilterOpen}
        />

        <div className="relative bg-white">
          <PropertyListViewWrapper
            allPropertiesData={data}
            isFetchingNextPage={loading}
            isFilterOpen={isFilterOpen}
            onRequestImagesClick={handleRequestImagesClick}
            propertyType="private-properties"
          />
        </div>
      </PropertyListPageWrapper>
    </>
  );
};
